import * as React from "react";
import { useLiveQuery } from "dexie-react-hooks";
import { db, type AuthRecord } from "@/lib/dexie-db";
import { clearAuth, saveAuth } from "@/lib/api-client";
import { resetQueryStateForLogout } from "@/lib/query-client";
import { unregisterServiceWorker } from "@/lib/sw-registration";
import { toast } from "@/components/ui/toaster";

/**
 * AuthContext — exposes the current dexie auth-row + login/logout helpers.
 * Subscribed via useLiveQuery, so the UI flips immediately after a magic-
 * link verify or a logout.
 */

export interface AuthState {
  status: "loading" | "authenticated" | "anonymous";
  user?: AuthRecord["user"];
  login: (record: AuthRecord) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = React.createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }): React.ReactElement {
  const record = useLiveQuery(() => db.auth.get("current"), [], undefined);
  // useLiveQuery returns undefined while loading AND when the table is empty.
  // We distinguish by tracking whether at least one query has resolved.
  const [initialized, setInitialized] = React.useState(false);
  React.useEffect(() => {
    if (record !== undefined || (record === undefined && !initialized)) {
      setInitialized(true);
    }
  }, [record, initialized]);

  // External logout-trigger from api-client (refresh failed → 401 cascade).
  // Fall A (reason:"expired"): info-Toast feuern bevor der redirect passiert.
  React.useEffect(() => {
    const handler = (event: Event) => {
      const detail = (event as CustomEvent<{ reason?: string }>).detail;
      if (detail?.reason === "expired") {
        toast(
          "Ihre Anmeldung ist abgelaufen. Bitte melden Sie sich erneut an.",
          "info",
          6000,
        );
      }
      void resetQueryStateForLogout();
    };
    window.addEventListener("hitzeprotokoll:logout", handler);
    return () => window.removeEventListener("hitzeprotokoll:logout", handler);
  }, []);

  const login = React.useCallback(async (next: AuthRecord) => {
    await saveAuth(next);
  }, []);

  const logout = React.useCallback(async () => {
    await clearAuth();
    await resetQueryStateForLogout();
  }, []);

  const status: AuthState["status"] = !initialized
    ? "loading"
    : record
      ? "authenticated"
      : "anonymous";

  const value: AuthState = React.useMemo(
    () => ({
      status,
      user: record?.user,
      login,
      logout,
    }),
    [status, record, login, logout]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = React.useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used within <AuthProvider>");
  }
  return ctx;
}

/**
 * Hard-reset für die settings "Alle lokalen Daten löschen"-aktion.
 * Reihenfolge wichtig: dexie zuerst, dann query-cache, dann SW.
 */
export async function performHardLogout(): Promise<void> {
  await clearAuth();
  await resetQueryStateForLogout();
  await unregisterServiceWorker();
}
